import React, { useCallback, useState, useEffect } from 'react';
import ReactFlow, {
  Node,
  Edge,
  Controls,
  Background,
  BackgroundVariant,
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  ConnectionMode,
  Panel,
  NodeProps,
  Handle,
  Position,
  MarkerType,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import {
  MapPin,
  Package,
  Gift,
  Trash2,
  CheckCircle2,
  PlayCircle,
  Save,
  FolderOpen,
  FilePlus,
  Download,
  X,
  ChevronDown,
} from 'lucide-react';

// Types
type NodeType = 'quest_start' | 'marker' | 'item_check' | 'item_give' | 'item_remove' | 'quest_complete';

type BaseQuestNode = {
  id: string;
  type: NodeType;
  label: string;
  position: { x: number; y: number };
};

type QuestStartNodeData = BaseQuestNode & {
  type: 'quest_start';
  next?: string;
};

type MarkerNodeData = BaseQuestNode & {
  type: 'marker';
  coords: { x: number; y: number; z: number };
  radius: number;
  marker_type: string;
  icon: string;
  color: string;
  trigger_dialog?: string;
  on_enter_event?: string;
  next?: string;
};

type ItemCheckNodeData = BaseQuestNode & {
  type: 'item_check';
  item_id: string;
  quantity: number;
  check_mode: 'add' | 'remove' | 'has';
  check_interval: number;
  on_success?: string;
  on_failure?: string;
};

type ItemGiveNodeData = BaseQuestNode & {
  type: 'item_give';
  item_id: string;
  quantity: number;
  show_notification: boolean;
  next?: string;
};

type ItemRemoveNodeData = BaseQuestNode & {
  type: 'item_remove';
  item_id: string;
  quantity: number;
  next?: string;
};

type QuestCompleteNodeData = BaseQuestNode & {
  type: 'quest_complete';
  rewards: {
    money: number;
    exp: number;
    items: Array<{ id: string; quantity: number }>;
  };
  trigger_quest?: string;
  show_notification: boolean;
  notification_text: string;
};

type QuestNodeData =
  | QuestStartNodeData
  | MarkerNodeData
  | ItemCheckNodeData
  | ItemGiveNodeData
  | ItemRemoveNodeData
  | QuestCompleteNodeData;

type QuestData = {
  metadata: {
    id: string;
    name: string;
    description: string;
    required_quests: string[];
    auto_start: boolean;
    min_level: number;
  };
  nodes: Record<string, QuestNodeData>;
};

type SavedProject = {
  id: string;
  name: string;
  updated: number;
  data: QuestData;
};

const uid = (prefix = 'node') => `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;

// Quest Start Node
function QuestStartNode({ data, selected }: NodeProps) {
  const node = data.node as QuestStartNodeData;

  return (
    <div
      className={`rounded-full border-4 bg-cyan-50 shadow-xl w-32 h-32 flex items-center justify-center transition-all ${
        selected ? 'border-cyan-500 ring-4 ring-cyan-200' : 'border-cyan-400'
      }`}
    >
      <div className="text-center">
        <PlayCircle className="w-10 h-10 text-cyan-600 mx-auto mb-1" />
        <div className="text-xs font-bold text-cyan-900">СТАРТ</div>
        <div className="text-[10px] text-cyan-600 truncate px-2">{node.label}</div>
      </div>
      <Handle type="source" position={Position.Right} className="w-4 h-4 !bg-cyan-500 !border-2 !border-white" />
    </div>
  );
}

// Marker Node
function MarkerNode({ data, selected }: NodeProps) {
  const node = data.node as MarkerNodeData;

  return (
    <div
      className={`rounded-xl border-2 shadow-lg min-w-[260px] max-w-[300px] transition-all bg-purple-50 ${
        selected ? 'border-purple-500 ring-4 ring-purple-200' : 'border-purple-400'
      }`}
    >
      <Handle type="target" position={Position.Left} className="w-4 h-4 !bg-purple-500 !border-2 !border-white" />
      <div className="p-3 space-y-2">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-white/80 shadow-sm">
            <MapPin className="w-4 h-4 text-purple-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-bold uppercase tracking-wide text-purple-600">Маркер</div>
            <div className="text-sm font-semibold text-purple-900 truncate">{node.label}</div>
          </div>
        </div>
        <div className="text-xs bg-white/70 rounded p-2 space-y-1 font-mono text-purple-900">
          <div className="flex gap-1">
            <span className="opacity-60">Координаты:</span>
            <span className="font-semibold">
              X:{node.coords.x} Y:{node.coords.y} Z:{node.coords.z}
            </span>
          </div>
          <div className="flex gap-1">
            <span className="opacity-60">Радиус:</span>
            <span className="font-semibold">{node.radius}м</span>
          </div>
          {node.trigger_dialog && (
            <div className="flex gap-1">
              <span className="opacity-60">Диалог:</span>
              <span className="font-semibold truncate">{node.trigger_dialog}</span>
            </div>
          )}
        </div>
      </div>
      <Handle type="source" position={Position.Right} className="w-4 h-4 !bg-purple-500 !border-2 !border-white" />
    </div>
  );
}

// Item Check Node
function ItemCheckNode({ data, selected }: NodeProps) {
  const node = data.node as ItemCheckNodeData;

  return (
    <div
      className={`rounded-xl border-2 shadow-lg min-w-[260px] max-w-[300px] transition-all bg-orange-50 ${
        selected ? 'border-orange-500 ring-4 ring-orange-200' : 'border-orange-400'
      }`}
    >
      <Handle type="target" position={Position.Left} className="w-4 h-4 !bg-orange-500 !border-2 !border-white" />
      <div className="p-3 space-y-2">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-white/80 shadow-sm">
            <Package className="w-4 h-4 text-orange-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-bold uppercase tracking-wide text-orange-600">Проверка предмета</div>
            <div className="text-sm font-semibold text-orange-900 truncate">{node.label}</div>
          </div>
        </div>
        <div className="text-xs bg-white/70 rounded p-2 space-y-1 font-mono text-orange-900">
          <div className="flex gap-1">
            <span className="opacity-60">Предмет:</span>
            <span className="font-semibold truncate">{node.item_id}</span>
          </div>
          <div className="flex gap-1">
            <span className="opacity-60">Кол-во:</span>
            <span className="font-semibold">{node.quantity}</span>
          </div>
          <div className="flex gap-1">
            <span className="opacity-60">Режим:</span>
            <span className="font-semibold">{node.check_mode}</span>
          </div>
        </div>
        <div className="flex items-center justify-between text-[10px] font-bold pt-1">
          <div className="text-green-600">Успех</div>
          <div className="text-red-600">Провал</div>
        </div>
      </div>
      <Handle
        type="source"
        position={Position.Right}
        id="success"
        className="w-4 h-4 !bg-green-500 !border-2 !border-white"
        style={{ top: '70%' }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="failure"
        className="w-4 h-4 !bg-red-500 !border-2 !border-white"
        style={{ top: '90%' }}
      />
    </div>
  );
}

// Item Give Node
function ItemGiveNode({ data, selected }: NodeProps) {
  const node = data.node as ItemGiveNodeData;

  return (
    <div
      className={`rounded-xl border-2 shadow-lg min-w-[240px] max-w-[280px] transition-all bg-green-50 ${
        selected ? 'border-green-500 ring-4 ring-green-200' : 'border-green-400'
      }`}
    >
      <Handle type="target" position={Position.Left} className="w-4 h-4 !bg-green-500 !border-2 !border-white" />
      <div className="p-3 space-y-2">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-white/80 shadow-sm">
            <Gift className="w-4 h-4 text-green-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-bold uppercase tracking-wide text-green-600">Выдать предмет</div>
            <div className="text-sm font-semibold text-green-900 truncate">{node.label}</div>
          </div>
        </div>
        <div className="text-xs bg-white/70 rounded p-2 space-y-1 font-mono text-green-900">
          <div className="flex gap-1">
            <span className="opacity-60">Предмет:</span>
            <span className="font-semibold truncate">{node.item_id}</span>
          </div>
          <div className="flex gap-1">
            <span className="opacity-60">Кол-во:</span>
            <span className="font-semibold">{node.quantity}</span>
          </div>
        </div>
      </div>
      <Handle type="source" position={Position.Right} className="w-4 h-4 !bg-green-500 !border-2 !border-white" />
    </div>
  );
}

// Item Remove Node
function ItemRemoveNode({ data, selected }: NodeProps) {
  const node = data.node as ItemRemoveNodeData;

  return (
    <div
      className={`rounded-xl border-2 shadow-lg min-w-[240px] max-w-[280px] transition-all bg-red-50 ${
        selected ? 'border-red-500 ring-4 ring-red-200' : 'border-red-400'
      }`}
    >
      <Handle type="target" position={Position.Left} className="w-4 h-4 !bg-red-500 !border-2 !border-white" />
      <div className="p-3 space-y-2">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-white/80 shadow-sm">
            <Trash2 className="w-4 h-4 text-red-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-bold uppercase tracking-wide text-red-600">Забрать предмет</div>
            <div className="text-sm font-semibold text-red-900 truncate">{node.label}</div>
          </div>
        </div>
        <div className="text-xs bg-white/70 rounded p-2 space-y-1 font-mono text-red-900">
          <div className="flex gap-1">
            <span className="opacity-60">Предмет:</span>
            <span className="font-semibold truncate">{node.item_id}</span>
          </div>
          <div className="flex gap-1">
            <span className="opacity-60">Кол-во:</span>
            <span className="font-semibold">{node.quantity}</span>
          </div>
        </div>
      </div>
      <Handle type="source" position={Position.Right} className="w-4 h-4 !bg-red-500 !border-2 !border-white" />
    </div>
  );
}

// Quest Complete Node
function QuestCompleteNode({ data, selected }: NodeProps) {
  const node = data.node as QuestCompleteNodeData;

  return (
    <div
      className={`rounded-full border-4 bg-emerald-50 shadow-xl w-32 h-32 flex items-center justify-center transition-all ${
        selected ? 'border-emerald-500 ring-4 ring-emerald-200' : 'border-emerald-400'
      }`}
    >
      <Handle type="target" position={Position.Left} className="w-4 h-4 !bg-emerald-500 !border-2 !border-white" />
      <div className="text-center">
        <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto mb-1" />
        <div className="text-xs font-bold text-emerald-900">ЗАВЕРШЕНИЕ</div>
        <div className="text-[10px] text-emerald-600 truncate px-2">{node.label}</div>
      </div>
    </div>
  );
}

const nodeTypes = {
  questStartNode: QuestStartNode,
  markerNode: MarkerNode,
  itemCheckNode: ItemCheckNode,
  itemGiveNode: ItemGiveNode,
  itemRemoveNode: ItemRemoveNode,
  questCompleteNode: QuestCompleteNode,
};

// LocalStorage helpers
const STORAGE_KEY = 'wisequest_quests';

function saveToLocalStorage(projects: SavedProject[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(projects));
}

function loadFromLocalStorage(): SavedProject[] {
  const data = localStorage.getItem(STORAGE_KEY);
  return data ? JSON.parse(data) : [];
}

export default function ImprovedQuestBuilder() {
  const [questData, setQuestData] = useState<QuestData>({
    metadata: {
      id: 'new_quest',
      name: 'Новый квест',
      description: 'Описание квеста',
      required_quests: [],
      auto_start: false,
      min_level: 1,
    },
    nodes: {
      quest_start: {
        id: 'quest_start',
        type: 'quest_start',
        label: 'Начало квеста',
        position: { x: 0, y: 0 },
        next: '',
      },
    },
  });

  const [selectedNodeId, setSelectedNodeId] = useState<string>('quest_start');
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [showProjectList, setShowProjectList] = useState(false);
  const [savedProjects, setSavedProjects] = useState<SavedProject[]>([]);
  const [lastSaved, setLastSaved] = useState<number>(Date.now());

  // Load projects on mount
  useEffect(() => {
    setSavedProjects(loadFromLocalStorage());
  }, []);

  // Auto-save every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      handleSave();
    }, 30000);
    return () => clearInterval(interval);
  }, [questData]);

  const handleSave = () => {
    const projects = loadFromLocalStorage();
    const existing = projects.findIndex((p) => p.id === questData.metadata.id);

    const project: SavedProject = {
      id: questData.metadata.id,
      name: questData.metadata.name,
      updated: Date.now(),
      data: questData,
    };

    if (existing >= 0) {
      projects[existing] = project;
    } else {
      projects.push(project);
    }

    saveToLocalStorage(projects);
    setSavedProjects(projects);
    setLastSaved(Date.now());
  };

  const handleLoad = (project: SavedProject) => {
    setQuestData(project.data);
    setSelectedNodeId('quest_start');
    setShowProjectList(false);
  };

  const handleDelete = (projectId: string) => {
    const projects = savedProjects.filter((p) => p.id !== projectId);
    saveToLocalStorage(projects);
    setSavedProjects(projects);
  };

  const handleNewProject = () => {
    setQuestData({
      metadata: {
        id: `quest_${Date.now()}`,
        name: 'Новый квест',
        description: 'Описание квеста',
        required_quests: [],
        auto_start: false,
        min_level: 1,
      },
      nodes: {
        quest_start: {
          id: 'quest_start',
          type: 'quest_start',
          label: 'Начало квеста',
          position: { x: 0, y: 0 },
          next: '',
        },
      },
    });
    setSelectedNodeId('quest_start');
  };

  const getReactFlowNodes = (): Node[] => {
    return Object.entries(questData.nodes).map(([nodeId, node], index) => {
      const col = index % 4;
      const row = Math.floor(index / 4);

      let nodeType = 'questStartNode';
      if (node.type === 'marker') nodeType = 'markerNode';
      if (node.type === 'item_check') nodeType = 'itemCheckNode';
      if (node.type === 'item_give') nodeType = 'itemGiveNode';
      if (node.type === 'item_remove') nodeType = 'itemRemoveNode';
      if (node.type === 'quest_complete') nodeType = 'questCompleteNode';

      return {
        id: nodeId,
        type: nodeType,
        position: { x: col * 380, y: row * 300 },
        data: { node },
      };
    });
  };

  const getReactFlowEdges = (): Edge[] => {
    const edges: Edge[] = [];

    Object.entries(questData.nodes).forEach(([nodeId, node]) => {
      // Handle simple 'next' connections
      if ('next' in node && node.next && questData.nodes[node.next]) {
        edges.push({
          id: `${nodeId}-next-${node.next}`,
          source: nodeId,
          target: node.next,
          animated: true,
          style: { stroke: '#6366f1', strokeWidth: 2 },
          markerEnd: { type: MarkerType.ArrowClosed, color: '#6366f1' },
        });
      }

      // Handle item_check success/failure connections
      if (node.type === 'item_check') {
        if (node.on_success && questData.nodes[node.on_success]) {
          edges.push({
            id: `${nodeId}-success-${node.on_success}`,
            source: nodeId,
            sourceHandle: 'success',
            target: node.on_success,
            animated: true,
            label: 'Успех',
            style: { stroke: '#22c55e', strokeWidth: 3 },
            labelStyle: { fill: '#22c55e', fontWeight: 700 },
            labelBgStyle: { fill: '#f0fdf4' },
            markerEnd: { type: MarkerType.ArrowClosed, color: '#22c55e' },
          });
        }
        if (node.on_failure && questData.nodes[node.on_failure]) {
          edges.push({
            id: `${nodeId}-failure-${node.on_failure}`,
            source: nodeId,
            sourceHandle: 'failure',
            target: node.on_failure,
            animated: true,
            label: 'Провал',
            style: { stroke: '#ef4444', strokeWidth: 3 },
            labelStyle: { fill: '#ef4444', fontWeight: 700 },
            labelBgStyle: { fill: '#fef2f2' },
            markerEnd: { type: MarkerType.ArrowClosed, color: '#ef4444' },
          });
        }
      }
    });

    return edges;
  };

  const [nodes, setNodes, onNodesChange] = useNodesState(getReactFlowNodes());
  const [edges, setEdges, onEdgesChange] = useEdgesState(getReactFlowEdges());

  React.useEffect(() => {
    setNodes((currentNodes) => {
      const newNodes = getReactFlowNodes();

      // Preserve current positions from React Flow
      return newNodes.map(newNode => {
        const existingNode = currentNodes.find(n => n.id === newNode.id);
        if (existingNode) {
          // Keep the position from React Flow if node already exists
          return { ...newNode, position: existingNode.position };
        }
        return newNode;
      });
    });
  }, [questData]);

  React.useEffect(() => {
    setEdges(getReactFlowEdges());
  }, [questData, setEdges]);

  const onConnect = useCallback(
    (connection: Connection) => {
      if (connection.source && connection.target) {
        const sourceNode = questData.nodes[connection.source];
        const updatedNodes = { ...questData.nodes };

        if (sourceNode.type === 'item_check') {
          const itemCheckNode = updatedNodes[connection.source] as ItemCheckNodeData;
          if (connection.sourceHandle === 'success') {
            itemCheckNode.on_success = connection.target;
          } else if (connection.sourceHandle === 'failure') {
            itemCheckNode.on_failure = connection.target;
          }
        } else if ('next' in sourceNode) {
          (updatedNodes[connection.source] as any).next = connection.target;
        }

        setQuestData({ ...questData, nodes: updatedNodes });
      }

      setEdges((eds) => addEdge(connection, eds));
    },
    [questData, setEdges]
  );

  const onNodeClick = useCallback((_event: React.MouseEvent, node: Node) => {
    setSelectedNodeId(node.id);
  }, []);

  const onNodeDragStop = useCallback(
    (_event: React.MouseEvent, node: Node) => {
      // Save node position when dragging stops
      setQuestData((prev) => ({
        ...prev,
        nodes: {
          ...prev.nodes,
          [node.id]: {
            ...prev.nodes[node.id],
            position: node.position,
          },
        },
      }));
    },
    []
  );

  const addNode = (type: NodeType) => {
    const newId = uid(type);
    let newNode: QuestNodeData;

    // Calculate new node position
    let newPosition = { x: 250, y: 100 };

    if (selectedNodeId && questData.nodes[selectedNodeId]) {
      // Place to the right of selected node with offset
      const selectedNode = questData.nodes[selectedNodeId];
      newPosition = {
        x: selectedNode.position.x + 300,
        y: selectedNode.position.y + Math.random() * 50 - 25, // Small random offset
      };
    } else {
      // Place in center with small random offset
      newPosition = {
        x: 250 + Math.random() * 100,
        y: 100 + Object.keys(questData.nodes).length * 50,
      };
    }

    switch (type) {
      case 'quest_start':
        newNode = {
          id: newId,
          type: 'quest_start',
          label: 'Начало квеста',
          position: newPosition,
          next: '',
        };
        break;
      case 'marker':
        newNode = {
          id: newId,
          type: 'marker',
          label: 'Маркер на карте',
          position: newPosition,
          coords: { x: 0, y: 0, z: 0 },
          radius: 5,
          marker_type: 'default',
          icon: 'marker',
          color: '#FF0000',
          next: '',
        };
        break;
      case 'item_check':
        newNode = {
          id: newId,
          type: 'item_check',
          label: 'Проверка предмета',
          position: newPosition,
          item_id: 'item_id',
          quantity: 1,
          check_mode: 'has',
          check_interval: 1000,
          on_success: '',
          on_failure: '',
        };
        break;
      case 'item_give':
        newNode = {
          id: newId,
          type: 'item_give',
          label: 'Выдать предмет',
          position: newPosition,
          item_id: 'item_id',
          quantity: 1,
          show_notification: true,
          next: '',
        };
        break;
      case 'item_remove':
        newNode = {
          id: newId,
          type: 'item_remove',
          label: 'Забрать предмет',
          position: newPosition,
          item_id: 'item_id',
          quantity: 1,
          next: '',
        };
        break;
      case 'quest_complete':
        newNode = {
          id: newId,
          type: 'quest_complete',
          label: 'Завершение квеста',
          position: newPosition,
          rewards: {
            money: 1000,
            exp: 100,
            items: [],
          },
          show_notification: true,
          notification_text: 'Квест завершен!',
        };
        break;
      default:
        return;
    }

    setQuestData({
      ...questData,
      nodes: {
        ...questData.nodes,
        [newId]: newNode,
      },
    });
    setSelectedNodeId(newId);
    setShowAddMenu(false);
  };

  const deleteNode = (nodeId: string) => {
    if (nodeId === 'quest_start') return;

    const updatedNodes = { ...questData.nodes };
    delete updatedNodes[nodeId];

    // Clean up references
    Object.values(updatedNodes).forEach((node) => {
      if ('next' in node && node.next === nodeId) {
        (node as any).next = '';
      }
      if (node.type === 'item_check') {
        if (node.on_success === nodeId) node.on_success = '';
        if (node.on_failure === nodeId) node.on_failure = '';
      }
    });

    setQuestData({ ...questData, nodes: updatedNodes });
    setSelectedNodeId('quest_start');
  };

  const updateNode = (nodeId: string, updates: Partial<QuestNodeData>) => {
    // Preserve current position from React Flow nodes if not in updates
    const currentReactFlowNode = nodes.find(n => n.id === nodeId);
    const currentPosition = currentReactFlowNode?.position || questData.nodes[nodeId]?.position;

    setQuestData({
      ...questData,
      nodes: {
        ...questData.nodes,
        [nodeId]: {
          ...questData.nodes[nodeId],
          ...updates,
          // Preserve position unless explicitly updated
          position: updates.position || currentPosition,
        } as QuestNodeData,
      },
    });
  };

  const updateMetadata = (updates: Partial<QuestData['metadata']>) => {
    setQuestData({
      ...questData,
      metadata: { ...questData.metadata, ...updates },
    });
  };

  const exportQuest = () => {
    const blob = new Blob([JSON.stringify(questData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${questData.metadata.id}.quest.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const selectedNode = selectedNodeId ? questData.nodes[selectedNodeId] : null;
  const timeSinceLastSave = Math.floor((Date.now() - lastSaved) / 1000);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-slate-50">
      {/* Header */}
      <div className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50 shadow-sm">
        <div className="max-w-[1800px] mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-cyan-100 rounded-lg">
                <MapPin className="w-5 h-5 text-cyan-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-900">Редактор квестов</h1>
                <div className="flex items-center gap-3 text-sm text-slate-500">
                  <Input
                    value={questData.metadata.name}
                    onChange={(e) => updateMetadata({ name: e.target.value })}
                    className="h-6 text-xs font-medium w-48"
                  />
                  <span>• Сохранено {timeSinceLastSave}с назад</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-sm">
                <label className="text-slate-600">ID:</label>
                <Input
                  value={questData.metadata.id}
                  onChange={(e) => updateMetadata({ id: e.target.value })}
                  className="h-8 w-32 text-xs"
                />
              </div>
              <div className="flex items-center gap-2 text-sm">
                <label className="text-slate-600">Мин. уровень:</label>
                <Input
                  type="number"
                  value={questData.metadata.min_level}
                  onChange={(e) => updateMetadata({ min_level: parseInt(e.target.value) || 1 })}
                  className="h-8 w-16 text-xs"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="auto-start"
                  checked={questData.metadata.auto_start}
                  onChange={(e) => updateMetadata({ auto_start: e.target.checked })}
                  className="w-4 h-4"
                />
                <label htmlFor="auto-start" className="text-sm text-slate-600">
                  Авто-старт
                </label>
              </div>
              <Separator orientation="vertical" className="h-8" />
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={handleNewProject}>
                  <FilePlus className="w-4 h-4 mr-2" />
                  Новый
                </Button>
                <Button variant="outline" size="sm" onClick={() => setShowProjectList(true)}>
                  <FolderOpen className="w-4 h-4 mr-2" />
                  Загрузить
                </Button>
                <Button variant="outline" size="sm" onClick={handleSave}>
                  <Save className="w-4 h-4 mr-2" />
                  Сохранить
                </Button>
                <Button onClick={exportQuest} size="sm">
                  <Download className="w-4 h-4 mr-2" />
                  Экспорт
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Project List Modal */}
      {showProjectList && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Сохраненные квесты</CardTitle>
                  <CardDescription>Выберите квест для загрузки</CardDescription>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setShowProjectList(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="overflow-y-auto flex-1">
              {savedProjects.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <MapPin className="w-12 h-12 mx-auto mb-4 opacity-20" />
                  <p>Нет сохраненных квестов</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {savedProjects.map((project) => (
                    <div
                      key={project.id}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex-1">
                        <h3 className="font-semibold">{project.name}</h3>
                        <p className="text-sm text-slate-500">
                          ID: {project.id} • Обновлено: {new Date(project.updated).toLocaleString('ru')}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => handleLoad(project)}>
                          Загрузить
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => handleDelete(project.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-[1800px] mx-auto px-4 py-6">
        <div className="grid grid-cols-[300px_1fr_340px] gap-6">
          {/* Left Sidebar - Node Menu */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Узлы квеста</CardTitle>
              <CardDescription>Добавьте узлы на канвас</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Управление</div>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => addNode('quest_start')}
                >
                  <PlayCircle className="w-4 h-4 mr-2 text-cyan-600" />
                  Старт квеста
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => addNode('quest_complete')}
                >
                  <CheckCircle2 className="w-4 h-4 mr-2 text-emerald-600" />
                  Завершение
                </Button>
              </div>

              <Separator />

              <div className="space-y-2">
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Маркеры</div>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => addNode('marker')}
                >
                  <MapPin className="w-4 h-4 mr-2 text-purple-600" />
                  Маркер на карте
                </Button>
              </div>

              <Separator />

              <div className="space-y-2">
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Предметы</div>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => addNode('item_check')}
                >
                  <Package className="w-4 h-4 mr-2 text-orange-600" />
                  Проверка предмета
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => addNode('item_give')}
                >
                  <Gift className="w-4 h-4 mr-2 text-green-600" />
                  Выдать предмет
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => addNode('item_remove')}
                >
                  <Trash2 className="w-4 h-4 mr-2 text-red-600" />
                  Забрать предмет
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Center - ReactFlow Canvas */}
          <Card>
            <CardHeader>
              <CardTitle>Граф квеста</CardTitle>
              <CardDescription>{questData.metadata.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="w-full h-[700px] border-2 border-slate-200 rounded-lg bg-white">
                <ReactFlow
                  nodes={nodes}
                  edges={edges}
                  onNodesChange={onNodesChange}
                  onEdgesChange={onEdgesChange}
                  onConnect={onConnect}
                  onNodeClick={onNodeClick}
                  onNodeDragStop={onNodeDragStop}
                  nodeTypes={nodeTypes}
                  connectionMode={ConnectionMode.Loose}
                  fitView
                  snapToGrid
                  snapGrid={[15, 15]}
                >
                  <Controls />
                  <Background variant={BackgroundVariant.Dots} gap={15} size={1} color="#06b6d4" />
                </ReactFlow>
              </div>
            </CardContent>
          </Card>

          {/* Right Sidebar - Node Editor */}
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Метаданные квеста</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Описание</label>
                  <Textarea
                    value={questData.metadata.description}
                    onChange={(e) => updateMetadata({ description: e.target.value })}
                    rows={3}
                    className="text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Требуемые квесты (через запятую)</label>
                  <Input
                    value={questData.metadata.required_quests.join(', ')}
                    onChange={(e) =>
                      updateMetadata({
                        required_quests: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                      })
                    }
                    placeholder="quest_1, quest_2"
                    className="text-sm"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Node Editor */}
            {selectedNode && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-base">
                        {selectedNode.type === 'quest_start' && 'Старт квеста'}
                        {selectedNode.type === 'marker' && 'Маркер'}
                        {selectedNode.type === 'item_check' && 'Проверка предмета'}
                        {selectedNode.type === 'item_give' && 'Выдать предмет'}
                        {selectedNode.type === 'item_remove' && 'Забрать предмет'}
                        {selectedNode.type === 'quest_complete' && 'Завершение'}
                      </CardTitle>
                      <CardDescription className="text-xs">{selectedNodeId}</CardDescription>
                    </div>
                    {selectedNodeId !== 'quest_start' && (
                      <Button variant="destructive" size="sm" onClick={() => deleteNode(selectedNodeId)}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Quest Start Node Editor */}
                  {selectedNode.type === 'quest_start' && (
                    <>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Название</label>
                        <Input
                          value={selectedNode.label}
                          onChange={(e) => updateNode(selectedNodeId, { label: e.target.value })}
                          className="text-sm"
                        />
                      </div>
                    </>
                  )}

                  {/* Marker Node Editor */}
                  {selectedNode.type === 'marker' && (
                    <>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Название</label>
                        <Input
                          value={selectedNode.label}
                          onChange={(e) => updateNode(selectedNodeId, { label: e.target.value })}
                          className="text-sm"
                        />
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <div className="space-y-2">
                          <label className="text-xs font-medium">X</label>
                          <Input
                            type="number"
                            value={selectedNode.coords.x}
                            onChange={(e) =>
                              updateNode(selectedNodeId, {
                                coords: { ...selectedNode.coords, x: parseFloat(e.target.value) || 0 },
                              })
                            }
                            className="text-sm"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-xs font-medium">Y</label>
                          <Input
                            type="number"
                            value={selectedNode.coords.y}
                            onChange={(e) =>
                              updateNode(selectedNodeId, {
                                coords: { ...selectedNode.coords, y: parseFloat(e.target.value) || 0 },
                              })
                            }
                            className="text-sm"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-xs font-medium">Z</label>
                          <Input
                            type="number"
                            value={selectedNode.coords.z}
                            onChange={(e) =>
                              updateNode(selectedNodeId, {
                                coords: { ...selectedNode.coords, z: parseFloat(e.target.value) || 0 },
                              })
                            }
                            className="text-sm"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Радиус (м)</label>
                        <Input
                          type="number"
                          value={selectedNode.radius}
                          onChange={(e) => updateNode(selectedNodeId, { radius: parseFloat(e.target.value) || 0 })}
                          className="text-sm"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Тип маркера</label>
                        <Input
                          value={selectedNode.marker_type}
                          onChange={(e) => updateNode(selectedNodeId, { marker_type: e.target.value })}
                          className="text-sm"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Иконка</label>
                        <Input
                          value={selectedNode.icon}
                          onChange={(e) => updateNode(selectedNodeId, { icon: e.target.value })}
                          className="text-sm"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Цвет</label>
                        <Input
                          value={selectedNode.color}
                          onChange={(e) => updateNode(selectedNodeId, { color: e.target.value })}
                          className="text-sm"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Диалог (опционально)</label>
                        <Input
                          value={selectedNode.trigger_dialog || ''}
                          onChange={(e) => updateNode(selectedNodeId, { trigger_dialog: e.target.value })}
                          placeholder="dialog_id"
                          className="text-sm"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Событие при входе (опционально)</label>
                        <Input
                          value={selectedNode.on_enter_event || ''}
                          onChange={(e) => updateNode(selectedNodeId, { on_enter_event: e.target.value })}
                          placeholder="event_id"
                          className="text-sm"
                        />
                      </div>
                    </>
                  )}

                  {/* Item Check Node Editor */}
                  {selectedNode.type === 'item_check' && (
                    <>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Название</label>
                        <Input
                          value={selectedNode.label}
                          onChange={(e) => updateNode(selectedNodeId, { label: e.target.value })}
                          className="text-sm"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">ID предмета</label>
                        <Input
                          value={selectedNode.item_id}
                          onChange={(e) => updateNode(selectedNodeId, { item_id: e.target.value })}
                          className="text-sm"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Количество</label>
                        <Input
                          type="number"
                          value={selectedNode.quantity}
                          onChange={(e) => updateNode(selectedNodeId, { quantity: parseInt(e.target.value) || 1 })}
                          className="text-sm"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Режим проверки</label>
                        <select
                          className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                          value={selectedNode.check_mode}
                          onChange={(e) =>
                            updateNode(selectedNodeId, { check_mode: e.target.value as 'add' | 'remove' | 'has' })
                          }
                        >
                          <option value="has">Проверить наличие (has)</option>
                          <option value="add">Добавить (add)</option>
                          <option value="remove">Удалить (remove)</option>
                        </select>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Интервал проверки (мс)</label>
                        <Input
                          type="number"
                          value={selectedNode.check_interval}
                          onChange={(e) =>
                            updateNode(selectedNodeId, { check_interval: parseInt(e.target.value) || 1000 })
                          }
                          className="text-sm"
                        />
                      </div>
                      <div className="p-3 bg-orange-50 rounded-lg border border-orange-200 text-xs">
                        <div className="font-semibold text-orange-900 mb-2">Подключения:</div>
                        <div className="space-y-1 text-orange-700">
                          <div>🟢 Верхний выход (70%) → Успех</div>
                          <div>🔴 Нижний выход (90%) → Провал</div>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Item Give Node Editor */}
                  {selectedNode.type === 'item_give' && (
                    <>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Название</label>
                        <Input
                          value={selectedNode.label}
                          onChange={(e) => updateNode(selectedNodeId, { label: e.target.value })}
                          className="text-sm"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">ID предмета</label>
                        <Input
                          value={selectedNode.item_id}
                          onChange={(e) => updateNode(selectedNodeId, { item_id: e.target.value })}
                          className="text-sm"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Количество</label>
                        <Input
                          type="number"
                          value={selectedNode.quantity}
                          onChange={(e) => updateNode(selectedNodeId, { quantity: parseInt(e.target.value) || 1 })}
                          className="text-sm"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="show-notif"
                          checked={selectedNode.show_notification}
                          onChange={(e) => updateNode(selectedNodeId, { show_notification: e.target.checked })}
                          className="w-4 h-4"
                        />
                        <label htmlFor="show-notif" className="text-sm">
                          Показать уведомление
                        </label>
                      </div>
                    </>
                  )}

                  {/* Item Remove Node Editor */}
                  {selectedNode.type === 'item_remove' && (
                    <>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Название</label>
                        <Input
                          value={selectedNode.label}
                          onChange={(e) => updateNode(selectedNodeId, { label: e.target.value })}
                          className="text-sm"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">ID предмета</label>
                        <Input
                          value={selectedNode.item_id}
                          onChange={(e) => updateNode(selectedNodeId, { item_id: e.target.value })}
                          className="text-sm"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Количество</label>
                        <Input
                          type="number"
                          value={selectedNode.quantity}
                          onChange={(e) => updateNode(selectedNodeId, { quantity: parseInt(e.target.value) || 1 })}
                          className="text-sm"
                        />
                      </div>
                    </>
                  )}

                  {/* Quest Complete Node Editor */}
                  {selectedNode.type === 'quest_complete' && (
                    <>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Название</label>
                        <Input
                          value={selectedNode.label}
                          onChange={(e) => updateNode(selectedNodeId, { label: e.target.value })}
                          className="text-sm"
                        />
                      </div>
                      <Separator />
                      <div className="space-y-3">
                        <div className="text-sm font-semibold">Награды</div>
                        <div className="space-y-2">
                          <label className="text-xs font-medium">Деньги</label>
                          <Input
                            type="number"
                            value={selectedNode.rewards.money}
                            onChange={(e) =>
                              updateNode(selectedNodeId, {
                                rewards: { ...selectedNode.rewards, money: parseInt(e.target.value) || 0 },
                              })
                            }
                            className="text-sm"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-xs font-medium">Опыт</label>
                          <Input
                            type="number"
                            value={selectedNode.rewards.exp}
                            onChange={(e) =>
                              updateNode(selectedNodeId, {
                                rewards: { ...selectedNode.rewards, exp: parseInt(e.target.value) || 0 },
                              })
                            }
                            className="text-sm"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-xs font-medium">Предметы (JSON)</label>
                          <Textarea
                            value={JSON.stringify(selectedNode.rewards.items, null, 2)}
                            onChange={(e) => {
                              try {
                                const items = JSON.parse(e.target.value || '[]');
                                updateNode(selectedNodeId, {
                                  rewards: { ...selectedNode.rewards, items },
                                });
                              } catch {}
                            }}
                            rows={3}
                            className="text-xs font-mono"
                            placeholder='[{"id": "phone", "quantity": 1}]'
                          />
                        </div>
                      </div>
                      <Separator />
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Запустить квест (опционально)</label>
                        <Input
                          value={selectedNode.trigger_quest || ''}
                          onChange={(e) => updateNode(selectedNodeId, { trigger_quest: e.target.value })}
                          placeholder="next_quest_id"
                          className="text-sm"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="show-complete-notif"
                          checked={selectedNode.show_notification}
                          onChange={(e) => updateNode(selectedNodeId, { show_notification: e.target.checked })}
                          className="w-4 h-4"
                        />
                        <label htmlFor="show-complete-notif" className="text-sm">
                          Показать уведомление
                        </label>
                      </div>
                      {selectedNode.show_notification && (
                        <div className="space-y-2">
                          <label className="text-sm font-medium">Текст уведомления</label>
                          <Textarea
                            value={selectedNode.notification_text}
                            onChange={(e) => updateNode(selectedNodeId, { notification_text: e.target.value })}
                            rows={2}
                            className="text-sm"
                          />
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* JSON Preview */}
        <Card className="mt-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Экспорт JSON</CardTitle>
              <Button onClick={exportQuest} size="sm">
                <Download className="w-4 h-4 mr-2" />
                Скачать JSON
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <pre className="bg-slate-950 text-slate-50 p-4 rounded-lg overflow-auto max-h-[400px] text-xs font-mono">
              {JSON.stringify(questData, null, 2)}
            </pre>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
